import { observer } from 'mobx-react';
import React, { createRef, RefObject } from 'react';
import { Section } from 'controls/react/layout/section';
import { FormEntryNew } from '../../controls/react/form/formEntryNew';
import { RecurringEventForm, RecurringEventFormImpl, scheduleTranslates } from '../administration/calendarEventFormNew';
import RRule from 'rrule';
import { ApplicationState } from '../../framework/applicationState';
import { createRoot, Root } from 'react-dom/client';
import { translator } from 'core/localization/localization';
import { AntDatePicker } from '../../controls/react/ant/antDatePicker';
import moment from 'moment';
import { TimeZoneSelector } from '../../controls/react/TimeZoneSelector';
import { computed, makeAutoObservable, makeObservable, observable, reaction } from 'mobx';
import { ModelValidator } from '../../framework/mobx-integration';
import { parseDateFormat } from 'tools/dateTime/parseDateFormat';

const i18n = translator(scheduleTranslates, {
	'Time settings': {
		no: 'Tidsinnstillinger',
	},
});

export class ScheduleTimeSettingsModel {
	rruleObject: RRule;
	startDate: moment.Moment;
	timeZone: string;
	validator = new ModelValidator(this);

	constructor(options?: ScheduleTimeSettingsOptions) {
		this.timeZone = options?.timeZone ?? ApplicationState.timezone;

		this.startDate = moment(options?.startDateTime);

		if (options?.rrule) {
			this.rruleObject = RRule.fromString(options.rrule);
		}

		makeAutoObservable(this);
		this.validator.required('startDate').required('timeZone');
	}

	get data() {
		return {
			rrule: this.rruleObject.toString().replace('RRULE:', ''),
			startDateTime: this.startDate?.format('YYYY-MM-DDTHH:mm:ss'),
			timeZone: this.timeZone,
		};
	}
}

export class ScheduleTimeSettingsOptions {
	rrule: string;
	startDateTime: string;
	timeZone: string;
}

export class ScheduleTimeSettingsProps {
	value?: ScheduleTimeSettingsOptions;
	onChange?: (data: ScheduleTimeSettingsOptions) => void;
}

class ScheduleTimeSettingsStore {
	model: ScheduleTimeSettingsModel;
	rruleForm: RefObject<RecurringEventFormImpl>;
	rruleFormValid = true;

	get isValid() {
		return this.model.validator.valid && this.rruleFormValid;
	}
	constructor(private props: ScheduleTimeSettingsProps) {
		makeObservable(this, {
			model: observable,
			rruleFormValid: observable,
			isValid: computed,
		});
		this.rruleForm = createRef<RecurringEventFormImpl>();
		this.model = new ScheduleTimeSettingsModel(this.props.value);
		reaction(
			() => [this.model.rruleObject, this.model.startDate, this.model.timeZone],
			() => {
				this.props.onChange?.(this.model.data);
			},
		);
	}
}

export class ScheduleTimeSettingsImpl extends React.Component<ScheduleTimeSettingsProps> {
	store: ScheduleTimeSettingsStore;

	constructor(props: ScheduleTimeSettingsProps) {
		super(props);
		this.store = new ScheduleTimeSettingsStore(props);
	}

	render() {
		return (
			<>
				<Section appearance={'none'} contentPadding>
					<FormEntryNew label={i18n('Time zone')} model={this.store.model} modelField={'timeZone'}>
						<TimeZoneSelector />
					</FormEntryNew>
				</Section>
				<Section appearance={'none'} contentPadding>
					<FormEntryNew label={i18n('START')} model={this.store.model} modelField={'startDate'}>
						<AntDatePicker format={parseDateFormat(ApplicationState.dateTimeFormat)} showTime />
					</FormEntryNew>
				</Section>
				<FormEntryNew model={this.store.model} modelField={'rruleObject'}>
					<RecurringEventForm
						ref={this.store.rruleForm}
						onValidChange={(valid) => (this.store.rruleFormValid = valid)}
						timeZone={this.store.model.timeZone}
						startDate={this.store.model.startDate?.valueOf()}
					/>
				</FormEntryNew>
			</>
		);
	}
}
export const ScheduleTimeSettings = observer(ScheduleTimeSettingsImpl);

export const renderScheduleTimeSettings = (container: HTMLElement, options: ScheduleTimeSettingsProps) => {
	if (!container) {
		return null;
	}
	const root = createRoot(container);
	const result: { root: Root; component?: React.Component } = {
		root: root,
	};
	root.render(
		<Section title={i18n('Time settings')} appearance={'frame'}>
			<ScheduleTimeSettings
				ref={(cmp) => {
					result.component = cmp;
				}}
				{...options}
			/>
		</Section>,
	);
	return result;
};
